import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Plus,
    Pause,
    Play,
    Trash2,
    Pencil,
    UploadCloud,
    ImagePlus,
    Eye,
    MousePointerClick,
} from "lucide-react";
import { api, errorMessage, publicAssetUrl } from "@/lib/api";
import {
    listBanners,
    createBanner,
    updateBanner,
    pauseBanner,
    resumeBanner,
    deleteBanner,
    uploadImage,
    BANNER_PLACEMENTS,
    BANNER_STATUS_TONE,
} from "@/lib/ads";
import { formatDate } from "@/lib/format";

function StatusPill({ status }) {
    return (
        <Badge
            variant="outline"
            className={`text-[10px] uppercase ${BANNER_STATUS_TONE[status] || ""}`}
            data-testid={`banner-status-${status}`}
        >
            {status}
        </Badge>
    );
}

export default function BannersManagerPage() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [districts, setDistricts] = useState([]);
    const [filters, setFilters] = useState({ status: "", placement: "", district_slug: "" });
    const [createOpen, setCreateOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const r = await listBanners({ ...filters });
            setRows(r);
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        api.get("/districts").then((r) => setDistricts(r.data)).catch(() => {});
    }, []);
    useEffect(() => {
        load();
    }, [filters.status, filters.placement, filters.district_slug]); // eslint-disable-line

    const onPause = async (row) => {
        try {
            await pauseBanner(row.id);
            toast.success("Banner paused");
            load();
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };
    const onResume = async (row) => {
        try {
            await resumeBanner(row.id);
            toast.success("Banner resumed");
            load();
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };
    const onDelete = async (row, force = false) => {
        try {
            await deleteBanner(row.id, force);
            toast.success("Banner deleted");
            load();
        } catch (e) {
            const status = e?.response?.status;
            if (status === 409 && !force) {
                if (window.confirm(e?.response?.data?.detail + "\n\nForce delete?")) {
                    return onDelete(row, true);
                }
                return;
            }
            toast.error(errorMessage(e));
        }
    };

    return (
        <div data-testid="banners-manager-page">
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <div className="text-sm font-medium text-orange-600">Monetization · Banners</div>
                    <h1 className="mt-1 text-3xl font-extrabold text-neutral-900">Banner Ads</h1>
                    <p className="mt-1 text-sm text-neutral-500">
                        Global + district-scoped banners with impression/click tracking.
                    </p>
                </div>
                <BannerCreateEditDialog
                    open={createOpen || !!editing}
                    editing={editing}
                    onOpenChange={(v) => {
                        setCreateOpen(v);
                        if (!v) setEditing(null);
                    }}
                    onCreated={() => {
                        setCreateOpen(false);
                        setEditing(null);
                        load();
                    }}
                    districts={districts}
                    trigger={
                        <Button
                            className="nest-gradient text-white"
                            onClick={() => setCreateOpen(true)}
                            data-testid="banner-create-btn"
                        >
                            <Plus size={14} className="mr-2" /> Create Banner
                        </Button>
                    }
                />
            </div>

            <Card className="mb-4 rounded-2xl border border-neutral-200 p-3">
                <div className="flex flex-wrap gap-2">
                    <FilterSelect
                        placeholder="All statuses"
                        value={filters.status}
                        onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
                        items={["DRAFT", "SCHEDULED", "LIVE", "PAUSED", "EXPIRED"].map((s) => ({ value: s, label: s }))}
                        testid="banner-filter-status"
                    />
                    <FilterSelect
                        placeholder="All placements"
                        value={filters.placement}
                        onChange={(v) => setFilters((f) => ({ ...f, placement: v }))}
                        items={BANNER_PLACEMENTS.map((p) => ({ value: p.value, label: p.label }))}
                        testid="banner-filter-placement"
                    />
                    <FilterSelect
                        placeholder="All districts"
                        value={filters.district_slug}
                        onChange={(v) => setFilters((f) => ({ ...f, district_slug: v }))}
                        items={districts.map((d) => ({ value: d.slug, label: d.name }))}
                        testid="banner-filter-district"
                    />
                </div>
            </Card>

            <Card className="rounded-2xl border border-neutral-200">
                {loading ? (
                    <div className="space-y-2 p-4">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <Skeleton key={i} className="h-14 rounded-xl" />
                        ))}
                    </div>
                ) : rows.length === 0 ? (
                    <div className="py-16 text-center">
                        <ImagePlus size={32} className="mx-auto text-neutral-400" />
                        <div className="mt-2 text-sm font-semibold text-neutral-700">No banners yet</div>
                        <div className="text-xs text-neutral-500">Create your first banner to get started.</div>
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Preview</TableHead>
                                <TableHead>Title</TableHead>
                                <TableHead>Placement</TableHead>
                                <TableHead>District</TableHead>
                                <TableHead>Window</TableHead>
                                <TableHead>Sort</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Impr.</TableHead>
                                <TableHead>Clicks</TableHead>
                                <TableHead>CTR</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.map((r) => {
                                const ctr = r.impressions_count
                                    ? ((r.clicks_count / r.impressions_count) * 100).toFixed(1)
                                    : "0.0";
                                return (
                                    <TableRow key={r.id} data-testid={`banner-row-${r.id}`}>
                                        <TableCell>
                                            <img
                                                src={publicAssetUrl(r.image_url)}
                                                alt=""
                                                className="h-8 w-14 rounded object-cover"
                                                onError={(e) => {
                                                    e.currentTarget.style.background = "#eee";
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell className="max-w-[220px] truncate font-medium">{r.title}</TableCell>
                                        <TableCell className="text-xs">{r.placement}</TableCell>
                                        <TableCell className="text-xs capitalize">{r.district_slug || "Global"}</TableCell>
                                        <TableCell className="text-xs">
                                            {formatDate(r.start_date)} → {formatDate(r.end_date)}
                                        </TableCell>
                                        <TableCell className="text-xs">{r.sort_order}</TableCell>
                                        <TableCell><StatusPill status={r.status} /></TableCell>
                                        <TableCell className="text-xs">
                                            <span className="inline-flex items-center gap-1">
                                                <Eye size={11} /> {r.impressions_count}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-xs">
                                            <span className="inline-flex items-center gap-1">
                                                <MousePointerClick size={11} /> {r.clicks_count}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-xs font-semibold">{ctr}%</TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-1">
                                                {r.status === "LIVE" || r.status === "SCHEDULED" ? (
                                                    <Button
                                                        size="icon"
                                                        variant="ghost"
                                                        title="Pause"
                                                        onClick={() => onPause(r)}
                                                        data-testid={`banner-pause-${r.id}`}
                                                    >
                                                        <Pause size={14} />
                                                    </Button>
                                                ) : r.status === "PAUSED" ? (
                                                    <Button
                                                        size="icon"
                                                        variant="ghost"
                                                        title="Resume"
                                                        onClick={() => onResume(r)}
                                                        data-testid={`banner-resume-${r.id}`}
                                                    >
                                                        <Play size={14} />
                                                    </Button>
                                                ) : null}
                                                <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    title="Edit"
                                                    onClick={() => setEditing(r)}
                                                    data-testid={`banner-edit-${r.id}`}
                                                >
                                                    <Pencil size={14} />
                                                </Button>
                                                <AlertDialog>
                                                    <AlertDialogTrigger asChild>
                                                        <Button
                                                            size="icon"
                                                            variant="ghost"
                                                            title="Delete"
                                                            className="text-rose-600"
                                                            data-testid={`banner-delete-${r.id}`}
                                                        >
                                                            <Trash2 size={14} />
                                                        </Button>
                                                    </AlertDialogTrigger>
                                                    <AlertDialogContent>
                                                        <AlertDialogHeader>
                                                            <AlertDialogTitle>Delete "{r.title}"?</AlertDialogTitle>
                                                            <AlertDialogDescription>
                                                                {(r.status === "LIVE" || r.status === "EXPIRED")
                                                                    ? "This banner has gone LIVE; delete will force-remove it."
                                                                    : "This will permanently remove the banner."}
                                                            </AlertDialogDescription>
                                                        </AlertDialogHeader>
                                                        <AlertDialogFooter>
                                                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                            <AlertDialogAction
                                                                onClick={() => onDelete(r, r.status === "LIVE" || r.status === "EXPIRED")}
                                                                className="bg-rose-600 text-white hover:bg-rose-700"
                                                                data-testid={`banner-delete-confirm-${r.id}`}
                                                            >
                                                                Delete
                                                            </AlertDialogAction>
                                                        </AlertDialogFooter>
                                                    </AlertDialogContent>
                                                </AlertDialog>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </Card>
        </div>
    );
}

function FilterSelect({ placeholder, value, onChange, items, testid }) {
    return (
        <Select value={value || "__any__"} onValueChange={(v) => onChange(v === "__any__" ? "" : v)}>
            <SelectTrigger className="w-44 text-xs" data-testid={testid}>
                <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
                <SelectItem value="__any__">{placeholder}</SelectItem>
                {items.map((it) => (
                    <SelectItem key={it.value} value={it.value}>{it.label}</SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}

function BannerCreateEditDialog({ open, editing, onOpenChange, onCreated, districts, trigger }) {
    const isEdit = !!editing;
    const [form, setForm] = useState(() => defaultForm());
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        if (open) {
            setForm(editing ? { ...defaultForm(), ...editing, district_slug: editing.district_slug || "" } : defaultForm());
        }
    }, [open, editing]);

    function defaultForm() {
        const today = new Date().toISOString().slice(0, 10);
        const later = new Date(Date.now() + 30 * 86400 * 1000).toISOString().slice(0, 10);
        return {
            title: "",
            image_url: "",
            cta_url: "",
            district_slug: "",
            placement: "HOME_TOP",
            start_date: today,
            end_date: later,
            sort_order: 100,
            status: "SCHEDULED",
        };
    }

    const upload = async (file) => {
        try {
            setUploading(true);
            const r = await uploadImage(file, "banner");
            setForm((f) => ({ ...f, image_url: r.url }));
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setUploading(false);
        }
    };

    const submit = async () => {
        if (!form.title) return toast.error("Title required");
        if (!form.image_url) return toast.error("Please upload a banner image");
        try {
            setBusy(true);
            const payload = {
                ...form,
                district_slug: form.district_slug || null,
                cta_url: form.cta_url || null,
                sort_order: Number(form.sort_order) || 0,
            };
            if (isEdit) {
                await updateBanner(editing.id, payload);
                toast.success("Banner updated");
            } else {
                await createBanner(payload);
                toast.success("Banner created");
            }
            onCreated?.();
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            {!isEdit && trigger}
            <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="banner-form-dialog">
                <DialogHeader>
                    <DialogTitle>{isEdit ? "Edit banner" : "Create a banner"}</DialogTitle>
                    <DialogDescription>Configure placement, timing and creative.</DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 md:grid-cols-2">
                    {/* Image upload */}
                    <div className="md:col-span-2">
                        <Label>Banner image *</Label>
                        <div className="mt-1 rounded-xl border-2 border-dashed border-neutral-300 p-3">
                            {form.image_url ? (
                                <img
                                    src={publicAssetUrl(form.image_url)}
                                    alt=""
                                    className="max-h-48 w-full rounded-lg object-cover"
                                />
                            ) : (
                                <div className="flex flex-col items-center py-6 text-neutral-500">
                                    <UploadCloud size={24} />
                                    <div className="mt-1 text-xs">PNG / JPG · 16:9 recommended</div>
                                </div>
                            )}
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
                                className="mt-2 block w-full text-xs"
                                data-testid="banner-image-input"
                            />
                            {uploading && <div className="mt-1 text-xs text-orange-600">Uploading…</div>}
                        </div>
                    </div>
                    <div>
                        <Label>Title *</Label>
                        <Input
                            maxLength={120}
                            value={form.title}
                            onChange={(e) => setForm({ ...form, title: e.target.value })}
                            data-testid="banner-title-input"
                        />
                    </div>
                    <div>
                        <Label>CTA URL</Label>
                        <Input
                            placeholder="/posts or https://…"
                            value={form.cta_url || ""}
                            onChange={(e) => setForm({ ...form, cta_url: e.target.value })}
                            data-testid="banner-cta-input"
                        />
                    </div>
                    <div>
                        <Label>Placement *</Label>
                        <Select value={form.placement} onValueChange={(v) => setForm({ ...form, placement: v })}>
                            <SelectTrigger data-testid="banner-placement-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {BANNER_PLACEMENTS.map((p) => (
                                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>District</Label>
                        <Select value={form.district_slug || "__all__"} onValueChange={(v) => setForm({ ...form, district_slug: v === "__all__" ? "" : v })}>
                            <SelectTrigger data-testid="banner-district-select"><SelectValue placeholder="All districts (global)" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__all__">All districts (global)</SelectItem>
                                {districts.map((d) => (
                                    <SelectItem key={d.slug} value={d.slug}>{d.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Start date *</Label>
                        <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="banner-start-input" />
                    </div>
                    <div>
                        <Label>End date *</Label>
                        <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="banner-end-input" />
                    </div>
                    <div>
                        <Label>Sort order</Label>
                        <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} data-testid="banner-sort-input" />
                        <div className="mt-1 text-[10px] text-neutral-500">Higher shows first when multiple compete.</div>
                    </div>
                    <div>
                        <Label>Status</Label>
                        <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                            <SelectTrigger data-testid="banner-status-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="DRAFT">DRAFT (hold)</SelectItem>
                                <SelectItem value="SCHEDULED">SCHEDULED (auto-live on start date)</SelectItem>
                                <SelectItem value="LIVE">LIVE (force live)</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button
                        onClick={submit}
                        disabled={busy || uploading}
                        className="nest-gradient text-white"
                        data-testid="banner-submit-btn"
                    >
                        {busy ? "Saving…" : isEdit ? "Save changes" : "Create banner"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
